import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setTimeZone } from "../../../features/user/userSlice";
import "./profile.css";
import camera from "../../../assets/icons/camera-outline.svg";
import goBackIcon from "../../../assets/icons/go-back.svg";
import editIcon from "../../../assets/icons/create-outline.svg";
import { MobileActionButton } from "../../../components/mobile-action-button/MobileActionButton";
import { MobileSubscritptionInput } from "../../../components/mobile-subscription-input/MobileSubscriptionInput";
import { BrowserLabel } from "../../../components/browser-label/BrowserLabel";
import { useNavigate } from "react-router-dom";
import { GreenBorderButton } from "../../../components/green-border-button/GreenBorderButton";
import cameraIcon from "../../../assets/icons/camera-outline.svg";
import { ItemShowData } from "../../../components/item-show-data/ItemShowData";
import {
  useGetEnterpriseProfileQuery,
  useUpdateEnterpriseProfileMutation,
  useUpdateEnterpriseDocumentCIFMutation,
} from "../../../features/enterprise/enterpriseApiSlice";
import texts from "../../../assets/texts.json";
import timeZones from "./timeZone.json";
import { setFeedback } from "../../../features/feedback/feedbackSlice";

const Profile = () => {
  const textsProfile = texts.views.profile;
  const [edit, setEdit] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({});
  const [selectedFileName, setSelectedFileName] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentTimeZone = useSelector(
    (state) => state.user.userConfigure?.timeZone || "UTC-06:00"
  ); // Obtenemos desde Redux
  const {
    data: enterpriseData,
    isLoading,
    error,
    refetch,
  } = useGetEnterpriseProfileQuery();
  const [updateProfile] = useUpdateEnterpriseProfileMutation();
  const [updateDocumentCIF] = useUpdateEnterpriseDocumentCIFMutation();
  const timezones = timeZones;

  const handleInputChange = (e, field) => {
    if (field === "documentCFI") {
      const file = e.target.files[0];
      console.log("Archivo seleccionado:", file);
      console.log("Archivo seleccionado:", selectedFileName);
      if (file) {
        // Validar que sea un PDF
        if (file.type !== "application/pdf") {
          dispatch(
            setFeedback({
              showFeedback: true,
              title: "Error",
              message: "Por favor, sube un archivo PDF",
              type: "error",
            })
          );
          return;
        }
        console.log("Archivo PDF válido:", {
          nombre: file.name,
          tipo: file.type,
          tamaño: file.size,
        });
        setFormData((prev) => ({
          ...prev,
          [field]: file,
        }));
      }
    } else {
      const value = e.target.value;
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));

      // Si es el campo de zona horaria, calcular y mostrar en consola
      if (field === "timeZone") {
        calculateTimeZoneChange(value);
      }
    }
  };

  const VALID_LANGUAGES = ["spanish", "english", "deutsch"];
  const VALID_APPEARANCES = ["light", "dark"];

  const handleSave = async (field) => {
    try {
      if (!formData[field]) {
        throw new Error("Field value is required");
      }

      let updateData = {};
      switch (field) {
        case "name":
          if (formData[field].trim().length < 1) {
            throw new Error("Name cannot be empty");
          }
          updateData = { name: formData[field].trim() };
          break;
        case "email":
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formData[field])) {
            throw new Error("Invalid email format");
          }
          updateData = { email: formData[field].toLowerCase() };
          break;
        case "address":
          if (formData[field].trim().length < 1) {
            throw new Error("Address cannot be empty");
          }
          updateData = {
            contact: {
              ...(enterpriseData?.data?.contact || {}),
              address: formData[field].trim(),
            },
          };
          break;
        case "language":
          if (!VALID_LANGUAGES.includes(formData[field])) {
            throw new Error(
              `Invalid language. Valid options: ${VALID_LANGUAGES.join(", ")}`
            );
          }
          updateData = { language: formData[field] };
          break;
        case "appearance":
          if (!VALID_APPEARANCES.includes(formData[field])) {
            throw new Error(
              `Invalid appearance. Valid options: ${VALID_APPEARANCES.join(
                ", "
              )}`
            );
          }
          updateData = { appearance: formData[field] };
          break;
        case "timeZone":
          updateData = { timeZone: formData[field] };
          break;
        case "documentCFI":
          try {
            console.log("Archivo a subir:", formData[field]);
            const formDataFile = new FormData();

            // Asegurarnos de que el archivo existe
            if (!formData[field] || !(formData[field] instanceof File)) {
              throw new Error("No se ha seleccionado un archivo válido");
            }

            // Agregar el archivo al FormData
            formDataFile.append("file", formData[field]);

            // Verificar el contenido del FormData
            console.log("FormData antes de enviar:");
            for (let pair of formDataFile.entries()) {
              console.log("Campo:", pair[0], "Valor:", pair[1]);
            }

            console.log("Iniciando subida del documento...");
            try {
              const result = await updateDocumentCIF(formDataFile).unwrap();
              console.log("Respuesta del servidor:", result);

              if (result.success) {
                // Refetch los datos del perfil para obtener la información actualizada
                await refetch();
                setEditingField(null);
                setFormData((prev) => ({ ...prev, [field]: "" }));
                setSelectedFileName(""); // Limpiar el nombre del archivo seleccionado
                dispatch(
                  setFeedback({
                    showFeedback: true,
                    title: "Documento subido exitosamente",
                    type: "success",
                  })
                );
                return;
              } else {
                throw new Error(
                  result.message || "Error en la respuesta del servidor"
                );
              }
            } catch (uploadError) {
              console.error("Error en la subida:", uploadError);
              if (uploadError.data) {
                console.error("Datos del error:", uploadError.data);
                throw new Error(
                  uploadError.data.message || "Error al subir el documento"
                );
              }
              throw uploadError;
            }
          } catch (error) {
            console.error("Error detallado al subir el documento:", error);
            console.error("Stack trace:", error.stack);
            dispatch(
              setFeedback({
                showFeedback: true,
                title: "Error",
                message:
                  error.message ||
                  "Error al subir el documento. Por favor, intenta de nuevo.",
                type: "error",
              })
            );
          }
          break;
        default:
          throw new Error(`Unsupported field: ${field}`);
      }

      const result = await updateProfile(updateData).unwrap();
      if (result.success) {
        setEditingField(null);
        setFormData((prev) => ({ ...prev, [field]: "" }));
        if (field === "timeZone") {
          dispatch(setTimeZone(formData[field])); // Actualizamos Redux
        }
      } else {
        throw new Error(result.message || "Update failed");
      }
    } catch (err) {
      console.error("Failed to update:", err.message);
    }
  };

  const getFullAddress = () => {
    if (!enterpriseData?.data?.contact) return "Sin dirección";

    const contact = enterpriseData.data.contact;
    const addressParts = [
      contact.street,
      contact.buildingNumber,
      contact.city,
      contact.state,
      contact.postalCode,
      contact.country,
    ].filter(Boolean);

    return addressParts.length > 0 ? addressParts.join(", ") : "Sin dirección";
  };

  // Función para calcular el cambio de zona horaria
  const calculateTimeZoneChange = (targetTimeZone) => {
    try {
      // Fecha base: mañana a las 4 PM en México (UTC-06:00, Central Time)
      const mexicoTimeZone = "America/Mexico_City"; // IANA timezone para México
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1); // Mañana
      tomorrow.setHours(16, 0, 0, 0); // 4 PM

      // Formato de fecha en México
      const mexicoDate = new Intl.DateTimeFormat("es-MX", {
        timeZone: mexicoTimeZone,
        dateStyle: "full",
        timeStyle: "long",
      }).format(tomorrow);

      // Formato de fecha en la zona horaria seleccionada
      // Convertimos tu timezone (UTC offset) a un IANA aproximado
      const ianaTimeZone = mapOffsetToIana(targetTimeZone);
      const targetDate = new Intl.DateTimeFormat("es-MX", {
        timeZone: ianaTimeZone,
        dateStyle: "full",
        timeStyle: "long",
      }).format(tomorrow);

      console.log(`Hora en México (${mexicoTimeZone}): ${mexicoDate}`);
      console.log(`Hora en ${targetTimeZone} (${ianaTimeZone}): ${targetDate}`);
    } catch (err) {
      console.error("Error calculando zona horaria:", err.message);
    }
  };

  // Mapeo aproximado de offsets UTC a zonas horarias IANA
  const mapOffsetToIana = (offset) => {
    const offsetMap = {
      "UTC-12:00": "Pacific/Midway",
      "UTC-11:00": "Pacific/Niue",
      "UTC-10:00": "Pacific/Honolulu",
      "UTC-09:00": "America/Anchorage",
      "UTC-08:00": "America/Los_Angeles",
      "UTC-07:00": "America/Denver",
      "UTC-06:00": "America/Mexico_City",
      "UTC-05:00": "America/New_York",
      "UTC-04:00": "America/Caracas",
      "UTC-03:00": "America/Buenos_Aires",
      "UTC-02:00": "America/Noronha",
      "UTC-01:00": "Atlantic/Azores",
      "UTC+00:00": "Europe/London",
      "UTC+01:00": "Europe/Paris",
      "UTC+02:00": "Africa/Cairo",
      "UTC+03:00": "Europe/Moscow",
      "UTC+04:00": "Asia/Dubai",
      "UTC+05:00": "Asia/Karachi",
      "UTC+06:00": "Asia/Dhaka",
      "UTC+07:00": "Asia/Bangkok",
      "UTC+08:00": "Asia/Singapore",
      "UTC+09:00": "Asia/Tokyo",
      "UTC+10:00": "Australia/Sydney",
      "UTC+11:00": "Pacific/Noumea",
      "UTC+12:00": "Pacific/Auckland",
    };
    return offsetMap[offset] || "UTC"; // Fallback a UTC si no coincide
  };

  const EditableField = ({ label, value, field, editingField }) => {
    const inputRef = useRef(null);
    const [selectedFileName, setSelectedFileName] = useState("");

    useEffect(() => {
      if (editingField === field && inputRef.current) {
        inputRef.current.focus();
      }
    }, [editingField, field]);

    const handleFileChange = (e) => {
      const file = e.target.files[0];
      if (file) {
        setSelectedFileName(file.name);
        handleInputChange(e, field);
      }
    };

    const renderInputField = (field, value) => {
      switch (field) {
        case "language":
          return (
            <select
              value={formData[field] || value || ""}
              onChange={(e) => handleInputChange(e, field)}
            >
              <option value="spanish">{textsProfile.spanishLanguage}</option>
              <option value="english">{textsProfile.englishLanguage}</option>
              <option value="deutsch">{textsProfile.germanLanguage}</option>
            </select>
          );
        case "appearance":
          return (
            <select
              value={formData[field] || value || ""}
              onChange={(e) => handleInputChange(e, field)}
            >
              <option value="light">{textsProfile.ligthModeOptionText}</option>
              <option value="dark">{textsProfile.darkModeOptionText}</option>
            </select>
          );
        case "timeZone":
          return (
            <select
              id="timezone-select"
              value={formData[field] || value || currentTimeZone} // Usamos Redux como fallback
              onChange={(e) => handleInputChange(e, field)}
            >
              {timezones.map((tz) => (
                <option key={tz.timezone} value={tz.timezone}>
                  {tz.timezone} - {tz.region}
                </option>
              ))}
            </select>
          );
        case "documentCFI":
          return (
            <div className="document-cfi-container">
              <div className="file-input-container">
                <input
                  ref={inputRef}
                  type="file"
                  accept=".pdf"
                  onChange={handleFileChange}
                  style={{ width: "100%", padding: "8px" }}
                />
                {selectedFileName && (
                  <div className="selected-file">
                    <p>Archivo seleccionado: {selectedFileName}</p>
                  </div>
                )}
              </div>
              {enterpriseData?.data?.billing?.taxId && (
                <div className="document-preview">
                  <p>
                    Documento actual:{" "}
                    {enterpriseData?.data?.billing?.taxId.split("_").pop()}
                  </p>
                </div>
              )}
            </div>
          );

        default:
          return (
            <input
              ref={inputRef}
              type="text"
              value={formData[field] || value || ""}
              onChange={(e) => handleInputChange(e, field)}
              style={{ width: "100%", padding: "8px" }}
            />
          );
      }
    };

    return editingField === field ? (
      <div className="data-container-button">
        <ItemShowData
          label={label}
          value={
            <div className="input-container-edit-profile">
              {renderInputField(field, value)}
            </div>
          }
        />
        <div className="edit-profile-button-container">
          <GreenBorderButton
            text={"Guardar"}
            border={true}
            onClick={() => handleSave(field)}
          />
          <GreenBorderButton
            text={"Cancelar"}
            border={false}
            onClick={() => {
              setEditingField(null);
              setFormData((prev) => ({ ...prev, [field]: "" }));
            }}
          />
        </div>
      </div>
    ) : (
      <div className="data-container-button">
        {field === "documentCFI" ? (
          <ItemShowData
            label={label}
            value={
              enterpriseData?.data?.billing?.taxId
                ? enterpriseData.data.billing.taxId.split("_").pop()
                : "Subir Documento"
            }
          />
        ) : field === "language" ? (
          <ItemShowData
            label={label}
            value={
              value === "spanish"
                ? textsProfile.spanishLanguage
                : value === "english"
                ? textsProfile.englishLanguage
                : value === "deutsch"
                ? textsProfile.germanLanguage
                : value
            }
          />
        ) : field === "appearance" ? (
          <ItemShowData
            label={label}
            value={
              value === "light"
                ? textsProfile.ligthModeOptionText
                : value === "dark"
                ? textsProfile.darkModeOptionText
                : value
            }
          />
        ) : (
          <ItemShowData label={label} value={value} />
        )}
        <GreenBorderButton
          text={"Editar"}
          border={true}
          onClick={() => {
            setEditingField(field);
            setFormData((prev) => ({ ...prev, [field]: value }));
          }}
        />
      </div>
    );
  };

  if (isLoading) return <div>Cargando...</div>;
  if (error) return <div>Error al cargar los datos</div>;

  return (
    <>
      <div className="deskSubs-general">
        <div className="photo-button-container">
          <div className="blue-camera-icon-container">
            <img src={cameraIcon} alt="" />
          </div>
          <div className="button-container-to-change-profile-picture">
            <GreenBorderButton
              text={textsProfile.changePhotoButtonText}
              border={true}
            />
            <GreenBorderButton
              text={textsProfile.removePhotoButtonText}
              border={false}
            />
          </div>
        </div>
        <div className="company-data-list-container">
          <EditableField
            label={textsProfile.inputLabelName}
            value={enterpriseData?.data?.name}
            field="name"
          />

          <EditableField
            label={textsProfile.inputLabelEmail}
            value={enterpriseData?.data?.email}
            field="email"
          />

          <EditableField
            label={textsProfile.companyAddressInputLabel}
            value={getFullAddress()}
            field="address"
          />

          <EditableField
            label={textsProfile.languageInputLabel}
            value={enterpriseData?.data?.language}
            field="language"
          />

          <EditableField
            label={textsProfile.appearanceInputLabel}
            value={enterpriseData?.data?.appearance}
            field="appearance"
          />

          <EditableField
            label={textsProfile.timeZoneInputLabel}
            value={enterpriseData?.data?.timeZone}
            field="timeZone"
          />

          <EditableField
            label={textsProfile.documentCFI}
            value={
              enterpriseData?.data?.billing?.taxId
                ? enterpriseData.data.billing.taxId.split("_").pop()
                : "Subir Documento"
            }
            field="documentCFI"
          />
        </div>
        <div className="advanced-options-container">
          <BrowserLabel
            label={textsProfile.loginAndSecurityLabel}
            navigateTo={"/home/profile/login-and-security"}
          />
          <div className="container-with-grey-edges">
            <BrowserLabel
              label={textsProfile.advancedConfigurationLabel}
              navigateTo={"/home/profile/advanced-configuration"}
            />
          </div>
          <BrowserLabel
            label={textsProfile.travelRulesLabel}
            navigateTo={"/home/profile/trip-rules"}
          />
        </div>
      </div>
      <div className="general-container-profile">
        {!edit && (
          <>
            <div className="navigation-option-profile">
              <img src={goBackIcon} alt="" onClick={() => navigate("/home")} />
              <img src={editIcon} alt="" onClick={() => setEdit(true)} />
            </div>
            <section className="profile-general-information">
              <div className="profile-picture">
                <div>
                  <img src={camera} alt="" />
                </div>
              </div>
            </section>
            <EditableField
              label="Nombre"
              value={enterpriseData?.data?.name}
              field="name"
            />

            <EditableField
              label="Correo Electrónico"
              value={enterpriseData?.data?.email}
              field="email"
            />

            <EditableField
              label="Dirección de la Empresa"
              value={getFullAddress()}
              field="address"
            />

            <EditableField
              label="Idioma"
              value={enterpriseData?.data?.language}
              field="language"
            />
            <EditableField
              label={"Apariencia"}
              value={enterpriseData?.data?.appearance}
              field="appearance"
            />
            <div className="browser-tag-container">
              <BrowserLabel
                label={"Inicio de Sesión y Seguridad"}
                navigateTo={"/home/profile/login-and-security"}
              />
              <BrowserLabel
                label={"Configuración Avanzada"}
                navigateTo={"/home/profile/advanced-configuration"}
              />
              <BrowserLabel
                label={"Mis Pagos"}
                navigateTo={"/home/profile/my-payments"}
              />
            </div>
          </>
        )}
        {edit && (
          <>
            <section className="profile-general-information">
              <div className="profile-picture">
                <div></div>
              </div>
            </section>
            <section className="form-profile">
              <MobileSubscritptionInput label={"Nombre de la Empresa"} />
              <MobileSubscritptionInput label={"Direccion"} />
              <MobileSubscritptionInput label={"RFC"} />
              <MobileSubscritptionInput label={"Idioma"} />
            </section>
            <section>
              <MobileActionButton text={"Cancelar"} cancel={true} />
              <MobileActionButton
                text={"Guardar"}
                onClick={() => setEdit(false)}
              />
            </section>
          </>
        )}
      </div>
    </>
  );
};

export { Profile };
